import React, { useState, useEffect, useMemo } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import {
    Chip,
    Stack,
    Box,
    useTheme,
    useMediaQuery,
    Tooltip,
    Avatar,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { parseISO } from "date-fns";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import moment from "moment-timezone";

// Helper function for validation
function isValidDate(date) {
    return date instanceof Date && !isNaN(date);
}

// Data normalization function to convert API response to expected format
function normalizeDoors(data) {
    const toNumber = (val) => {
        if (val === null || val === undefined || val === "") {
            return null;
        }
        const parsed = Number(val);
        return Number.isNaN(parsed) ? null : parsed;
    };

    return data.map((door, idx) => {
        // DEBUG: Log the raw door data to see what fields are present
        if (idx === 0) {
            console.log("Raw door data from API:", door);
            console.log("Checked field in raw data:", door.checked);
        }

        const normalized = {
            id: door.id || idx + 1,
            work_order_number: door.work_order_number || "",
            count: door.count || 0,
            production_schedule: door.production_schedule || null,

            // Project hierarchy
            project: door.project || null,
            service: door.service || null,
            hse: door.hse || null,

            // Assignment
            assignee: door.assignee || null,
            created_by: door.created_by || null,
            updated_by: door.updated_by || null,

            // Lifecycle dates
            due: door.due || null,
            is_complete: !!door.is_complete,
            completed: door.completed || null,
            created: door.created || null,
            updated: door.updated || null,
            checked: door.checked || null,
            checked_by: door.checked_by || null,
            is_archived: !!door.is_archived,
            archived: door.archived || null,
            archived_by: door.archived_by || null,

            // Door specifications
            quantity: toNumber(door.quantity) || 1,
            width: toNumber(door.width),
            height: toNumber(door.height),
            hand: door.hand || "",
            swing: door.swing || "",
            door_type: door.door_type || null,
            notes: door.notes || "",

            // Attachments
            qr_code: door.qr_code || null,
            drawings: Array.isArray(door.drawings) ? door.drawings : [],

            // Notes
            comments: door.comments || "",
        };

        // Debug logging for checked status
        if (door.checked) {
            console.log(`Door ${door.id} checked value:`, door.checked);
        }

        return normalized;
    });
}

// Simple avatar helper for initials
function stringAvatar(name = "") {
    const trimmed = name.trim();
    const first = trimmed.split(" ")[0] || "";
    const initial = first.charAt(0) || "";
    return {
        children: initial,
        sx: {
            bgcolor: "primary.light",
            color: "primary.contrastText",
        },
    };
}

// Due Date / Completion Chip Component
function DueDateChip({ value, completed, completedDate, checkedBy }) {
    const theme = useTheme();

    const currentDate = useMemo(
        () => moment.tz(new Date(), "America/New_York").toDate(),
        []
    );

    // If completed, show completion information
    if (completed) {
        const completedParsed = completedDate ? parseISO(completedDate) : null;

        return (
            <Box
                sx={{
                    width: 120,
                    height: 48,
                    border: 1,
                    borderColor: "success.main",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                }}
            >
                <CheckCircleIcon
                    sx={{
                        fontSize: "1.2rem",
                        mr: 1,
                        color: "success.main",
                        flexShrink: 0,
                    }}
                />
                <Stack
                    spacing={0.2}
                    alignItems="flex-start"
                    sx={{ width: "100%", minHeight: 0 }}
                    justifyContent="center"
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            lineHeight: 1.2,
                            color: "success.main",
                        }}
                    >
                        Completed
                    </Typography>
                    <Divider sx={{ width: "100%" }} />
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.secondary",
                            fontWeight: 400,
                            fontSize: "0.65rem",
                            lineHeight: 1.1,
                        }}
                    >
                        {completedParsed && isValidDate(completedParsed)
                            ? moment(completedParsed).format("MM/DD/YYYY")
                            : "—"}
                    </Typography>
                </Stack>
            </Box>
        );
    }

    // Original due date logic for incomplete items
    const parsed = value ? parseISO(value) : null;
    const delta =
        parsed && isValidDate(parsed)
            ? Math.ceil(
                  (parsed.getTime() - currentDate.getTime()) /
                      (1000 * 3600 * 24)
              )
            : null;

    let dueMessage = "No Due Date";
    if (delta !== null) {
        if (delta === 0) dueMessage = "Today";
        else if (delta === 1) dueMessage = "Tomorrow";
        else if (delta === -1) dueMessage = "Yesterday";
        else if (delta < 0) dueMessage = `${Math.abs(delta)} days ago`;
        else dueMessage = `In ${delta} days`;
    }

    return (
        <Box
            sx={{
                width: 120,
                height: 48,
                border: 1,
                borderColor:
                    delta !== null && delta < 0
                        ? "warning.main"
                        : "primary.main",
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
            }}
        >
            <EventAvailableIcon
                sx={{
                    fontSize: "1.2rem",
                    mr: 1,
                    color:
                        delta !== null && delta < 0
                            ? "warning.main"
                            : "primary.main",
                    flexShrink: 0,
                }}
            />
            <Stack
                spacing={0.2}
                alignItems="flex-start"
                sx={{ width: "100%", minHeight: 0 }}
                justifyContent="center"
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        lineHeight: 1.2,
                        color:
                            delta !== null && delta < 0
                                ? "warning.main"
                                : "primary.main",
                    }}
                >
                    {dueMessage}
                </Typography>
                <Divider sx={{ width: "100%" }} />
                <Typography
                    variant="caption"
                    sx={{
                        color: "text.secondary",
                        fontWeight: 400,
                        fontSize: "0.65rem",
                        lineHeight: 1.1,
                    }}
                >
                    {parsed && isValidDate(parsed)
                        ? moment(parsed).format("MM/DD/YYYY")
                        : "—"}
                </Typography>
            </Stack>
        </Box>
    );
}

// Door Summary Component
function DoorSummary({ darkState, totalCount, openCount, totalQty, openQty }) {
    return (
        <Box
            sx={{
                p: { xs: 1, sm: 2 },
                border: darkState ? "1.5px solid" : "3px solid",
                borderColor: "primary.main",
                borderRadius: 2,
                bgcolor: "background.paper",
                width: "100%",
            }}
        >
            <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                Door Work Orders Summary
            </Typography>
            <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">Total Orders:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                        {totalCount}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">Pending Orders:</Typography>
                    <Typography
                        variant="body2"
                        fontWeight={500}
                        color="warning.main"
                    >
                        {openCount}
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">Total Doors:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                        {totalQty}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">Pending Doors:</Typography>
                    <Typography
                        variant="body2"
                        fontWeight={500}
                        color="warning.main"
                    >
                        {openQty}
                    </Typography>
                </Box>
            </Stack>
        </Box>
    );
}

// Main Door Order List Component
function DoorOrderList({
    handleOpenSnackbar,
    darkState,
    user,
    token,
    onSelectOrder,
    doorOrderList = [],
}) {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    const [items, setItems] = useState([]);

    // Use the passed doorOrderList instead of fetching
    useEffect(() => {
        console.log(
            "DoorOrderList useEffect triggered with doorOrderList:",
            doorOrderList
        );
        if (doorOrderList && doorOrderList.length > 0) {
            const normalized = normalizeDoors(doorOrderList);
            console.log("Normalized doors:", normalized);
            setItems(normalized);
        }
    }, [doorOrderList]);

    const openWO = (order) => {
        if (onSelectOrder) {
            // Pass the original order from doorOrderList (not the normalized one)
            const originalOrder = doorOrderList.find((d) => d.id === order.id);
            onSelectOrder(originalOrder || order);
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                mx: "auto",
                px: 0,
                py: { xs: 1, sm: 2, md: 4 },
            }}
        >
            {items.length === 0 && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 2 }}
                >
                    No door orders to display.
                </Typography>
            )}

            {items.length > 0 && (
                <List
                    sx={{
                        width: "100%",
                        bgcolor: "background.paper",
                        border: darkState ? "1.5px solid" : "3px solid",
                        borderRadius: 2,
                        pt: 0,
                        pb: 0,
                        borderColor: "primary.main",
                        overflowX: "hidden",
                    }}
                >
                    {items.map((order, i) => {
                        // DEBUG: Log checked status for each door
                        if (i === 0) {
                            console.log(
                                `First door ID ${order.id} - checked: ${order.checked}, is_complete: ${order.is_complete}`
                            );
                        }

                        const assigneeName = order.assignee
                            ? `${order.assignee.first_name || ""} ${order
                                  .assignee.last_name || ""}`.trim()
                            : "";

                        // Determine which project target is being used
                        const projectLike =
                            order.project || order.service || order.hse || {};
                        const targetType = order.project
                            ? "Project"
                            : order.service
                            ? "Service"
                            : order.hse
                            ? "HSE"
                            : "";

                        const dueChip = (
                            <DueDateChip
                                value={order.due}
                                completed={order.is_complete}
                                completedDate={order.completed}
                                checkedBy={order.checked_by}
                            />
                        );

                        return (
                            <div key={order.id || i}>
                                <ListItem
                                    disablePadding
                                    secondaryAction={
                                        !isMobile && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{ pr: 1 }}
                                            >
                                                {dueChip}
                                            </Stack>
                                        )
                                    }
                                >
                                    <ListItemButton
                                        onClick={() => openWO(order)}
                                        sx={{
                                            position: "relative",
                                            overflow: "hidden",
                                            minWidth: 0,
                                            opacity:
                                                order.checked ||
                                                order.is_complete
                                                    ? 0.45
                                                    : 1,
                                            bgcolor:
                                                order.checked ||
                                                order.is_complete
                                                    ? "action.hover"
                                                    : "background.paper",
                                            borderLeft:
                                                order.checked ||
                                                order.is_complete
                                                    ? 4
                                                    : undefined,
                                            borderLeftColor:
                                                order.checked ||
                                                order.is_complete
                                                    ? "success.light"
                                                    : undefined,
                                        }}
                                    >
                                        {(order.checked ||
                                            order.is_complete) && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    bottom: 0,
                                                    left: 0,
                                                    width: 4,
                                                    bgcolor: "success.main",
                                                    zIndex: 3,
                                                }}
                                            />
                                        )}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: 4,
                                                left: 6,
                                                width: 10,
                                                height: 10,
                                                borderRadius: "50%",
                                                bgcolor:
                                                    order.checked ||
                                                    order.is_complete
                                                        ? "success.main"
                                                        : "warning.main",
                                                zIndex: 2,
                                            }}
                                        />

                                        <ListItemText
                                            primaryTypographyProps={{
                                                component: "div",
                                                variant: "subtitle1",
                                                sx: {
                                                    fontWeight: 700,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                },
                                            }}
                                            primary={
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    sx={{ overflow: "hidden" }}
                                                >
                                                    <Chip
                                                        size="small"
                                                        variant="filled"
                                                        color="primary"
                                                        label={
                                                            projectLike.number ||
                                                            "—"
                                                        }
                                                        sx={{ maxWidth: 120 }}
                                                    />
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight={700}
                                                        noWrap
                                                        sx={{ maxWidth: "60%" }}
                                                    >
                                                        {projectLike.name ||
                                                            projectLike.number ||
                                                            order.work_order_number}
                                                    </Typography>
                                                </Stack>
                                            }
                                            secondaryTypographyProps={{
                                                component: "div",
                                            }}
                                            secondary={
                                                <>
                                                    <Stack
                                                        spacing={0.25}
                                                        sx={{
                                                            wordBreak:
                                                                "break-word",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color="text.primary"
                                                        >
                                                            Work Order Number:{" "}
                                                            {order.work_order_number ||
                                                                "—"}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Hand:{" "}
                                                            {order.hand || "—"}{" "}
                                                            | Swing:{" "}
                                                            {order.swing || "—"}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Size:{" "}
                                                            {order.width
                                                                ? parseFloat(
                                                                      order.width
                                                                  ) %
                                                                      1 ===
                                                                  0
                                                                    ? parseInt(
                                                                          order.width
                                                                      )
                                                                    : order.width
                                                                : "—"}
                                                            " x{" "}
                                                            {order.height
                                                                ? parseFloat(
                                                                      order.height
                                                                  ) %
                                                                      1 ===
                                                                  0
                                                                    ? parseInt(
                                                                          order.height
                                                                      )
                                                                    : order.height
                                                                : "—"}
                                                            "
                                                        </Typography>
                                                        {order.notes && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                Notes:{" "}
                                                                {order.notes}
                                                            </Typography>
                                                        )}
                                                        {assigneeName ? (
                                                            <Tooltip
                                                                title={`Assigned to ${assigneeName}`}
                                                                disableFocusListener
                                                                disableTouchListener
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        mt: 3,
                                                                        px: 1,
                                                                        py: 0.75,
                                                                        width:
                                                                            "fit-content",
                                                                        alignSelf:
                                                                            "flex-start",
                                                                        border: 1,
                                                                        borderColor:
                                                                            "divider",
                                                                        borderRadius: 1,
                                                                        bgcolor:
                                                                            "background.default",
                                                                        display:
                                                                            "inline-flex",
                                                                        alignItems:
                                                                            "center",
                                                                    }}
                                                                >
                                                                    <Stack
                                                                        direction="row"
                                                                        spacing={
                                                                            0.75
                                                                        }
                                                                        alignItems="center"
                                                                    >
                                                                        {(() => {
                                                                            const avatarProps = stringAvatar(
                                                                                assigneeName
                                                                            );
                                                                            return (
                                                                                <Avatar
                                                                                    {...avatarProps}
                                                                                    sx={{
                                                                                        ...avatarProps.sx,
                                                                                        width: 28,
                                                                                        height: 28,
                                                                                        fontSize: 12,
                                                                                    }}
                                                                                />
                                                                            );
                                                                        })()}
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                            noWrap
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            {
                                                                                assigneeName
                                                                            }
                                                                        </Typography>
                                                                    </Stack>
                                                                </Box>
                                                            </Tooltip>
                                                        ) : null}
                                                    </Stack>
                                                    {isMobile && (
                                                        <Box sx={{ mt: 2 }}>
                                                            {dueChip}
                                                        </Box>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                                {i < items.length - 1 && (
                                    <Divider sx={{ my: 0 }} />
                                )}
                            </div>
                        );
                    })}
                </List>
            )}
        </Box>
    );
}

export default DoorOrderList;
